import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import Dialog from '@material-ui/core/Dialog'

import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'

import { Grid, MenuItem, Radio } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { SendHorizontal, TriangleAlert } from 'lucide-react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AuthContext } from '../../context/Auth/AuthContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { ContactSelect } from '../ContactSelect'
import ExistingTicketDialog from '../ExistingTicketDialog'
import { InputField } from '../InputField'
import SelectInput from '../SelectInput'
import { UserPhoneCountry } from '../UserPhoneCountry'
import { useStyles } from './style'

const NewTicketModal = ({
  modalOpen,
  onClose,
  initialContact = null,
  isEditing = false,
  ticketId = null,
  preselectedWhatsappId = null
}) => {
  const [loading, setLoading] = useState(false)
  const [, setSearchParam] = useState('')
  const [selectedContact, setSelectedContact] = useState(null)
  const [selectedQueue, setSelectedQueue] = useState('')
  const [selectedUser, setSelectedUser] = useState('')
  const [countryCode, setCountryCode] = useState('')
  const [newContact, setNewContact] = useState(false)
  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [connections, setConnections] = useState([])
  const [queues, setQueues] = useState([])
  const [users, setUsers] = useState([])
  const [selectedConnection, setSelectedConnection] = useState('')
  const [contactTags, setContactTags] = useState([])
  const [modalOpenWarning, setModalOpenWarning] = useState(false)
  const [existingTicketDialogOpen, setExistingTicketDialogOpen] =
    useState(false)
  const [existingTicket, setExistingTicket] = useState(null)
  const [isNewContactFlow, setIsNewContactFlow] = useState(true)
  const phoneInputWrapperRef = useRef(null)
  const history = useHistory()
  const { user } = useContext(AuthContext)

  const classes = useStyles()

  const initialState = {
    name: '',
    number: '',
    department: '',
    attendant: '',
    channel: ''
  }
  const [newTicket, setNewTicket] = useState(initialState)

  const getInitialContactConnectionId = () => {
    return (
      preselectedWhatsappId ||
      initialContact?.preselectedWhatsappId ||
      initialContact?.whatsappId ||
      ''
    )
  }

  const applyInitialPhoneData = contactNumber => {
    const cleanNumber = String(contactNumber || '').replace(/\D/g, '')

    if (!cleanNumber) {
      setCountryCode('')
      setNumber('')
      return
    }

    const parsedPhone = parsePhoneNumberFromString(`+${cleanNumber}`)

    if (parsedPhone) {
      setCountryCode(`+${parsedPhone.countryCallingCode}`)
      setNumber(parsedPhone.nationalNumber || cleanNumber)
      return
    }

    setCountryCode('')
    setNumber(cleanNumber)
  }

  useEffect(() => {
    if (
      initialContact &&
      initialContact.id !== undefined &&
      initialContact.id !== null
    ) {
      setIsNewContactFlow(false)
      setSelectedContact(initialContact)
      setName(initialContact.name)

      if (initialContact.tags && initialContact.tags.length > 0) {
        setContactTags(initialContact.tags)
      }

      // Se há um usuário pré-selecionado (vindo de tarefas), defini-lo como atendente
      if (initialContact.preselectedUserId) {
        setSelectedUser(initialContact.preselectedUserId)
      }

      applyInitialPhoneData(initialContact.number)
      setNewContact(false)
    } else if (
      initialContact &&
      (initialContact.name || initialContact.number)
    ) {
      setIsNewContactFlow(true)
      // setOptions([])
      setSelectedContact(null)
      setName(initialContact.name || '')
      setContactTags([])
      setSelectedQueue('')
      setSelectedUser(initialContact.preselectedUserId || '')
      applyInitialPhoneData(initialContact.number)
      setNewContact(true)
    } else {
      setIsNewContactFlow(true)
      setNewTicket(initialState)
      setName('')
      setNumber('')
      setCountryCode('')
      setNewContact(false)
      setSelectedContact(null)
      setContactTags([])
      setSelectedQueue('')
      setSelectedUser('')
      setCountryCode('')
    }
  }, [initialContact, modalOpen])

  const handleClose = () => {
    onClose()
    setSearchParam('')
    setSelectedContact(null)
    setContactTags([])
    setCountryCode('')
    setNumber('')
    setName('')
    setNewContact(false)
    setSelectedQueue('')
    setSelectedUser('')
  }

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      try {
        const [{ data: queueData }, { data: whatsappData }] = await Promise.all([
          api.get('/queue'),
          api.get('/whatsapp')
        ])

        const accessibleQueues = queueData.filter(
          queue => queue.notificationact !== true
        )

        // Apenas conexões explicitamente selecionadas em "Escolher Conexões"
        // podem ser usadas para criar atendimentos. Filas bot selecionadas em
        // "Escolher Bots" não devem expor a conexão aqui.
        const allowedConnections = whatsappData.filter(connection =>
          (user.whatsapps || []).some(w => w.id === connection.id)
        )

        const queuesFromAllowedConnections = allowedConnections.flatMap(
          connection => connection.queues || []
        )

        const mergedQueuesMap = new Map()
        accessibleQueues.forEach(q => mergedQueuesMap.set(q.id, q))
        queuesFromAllowedConnections.forEach(q => {
          if (!mergedQueuesMap.has(q.id)) {
            mergedQueuesMap.set(q.id, q)
          }
        })
        const mergedQueues = Array.from(mergedQueuesMap.values())

        if (!isMounted) return
        setQueues(mergedQueues)
        setConnections(allowedConnections)

        const initialConnectionId = getInitialContactConnectionId()

        if (
          initialConnectionId &&
          allowedConnections.some(c => c.id === initialConnectionId)
        ) {
          setSelectedConnection(initialConnectionId)
          return
        }

        if (allowedConnections.length === 1) {
          setSelectedConnection(allowedConnections[0].id)
          return
        }

        if (allowedConnections.length > 1) {
          const connection = allowedConnections.find(c => c.isDefault)
          if (connection) {
            setSelectedConnection(connection.id)
            return
          }
        }

        setSelectedConnection('')
      } catch (err) {
        if (isMounted) toastError(err)
      }
    }

    if (modalOpen) fetchData()
    return () => {
      isMounted = false
    }
  }, [initialContact, modalOpen, preselectedWhatsappId])

  useEffect(() => {
    if (!selectedConnection) {
      setSelectedQueue('')
      setSelectedUser('')
      return
    }

    const connection = connections.find(c => c.id === selectedConnection)
    if (!connection) {
      setSelectedQueue('')
      setSelectedUser('')
      return
    }

    const connectionQueueIds = new Set(
      (connection.queues || []).map(q => q.id)
    )
    const botQueueIds = queues
      .filter(q => q.isBot && connectionQueueIds.has(q.id))
      .map(q => q.id)

    const selectedQueueData = queues.find(q => q.id === selectedQueue)
    const stillValid =
      selectedQueueData &&
      !selectedQueueData.isBot &&
      botQueueIds.includes(selectedQueueData.botQueueId)

    if (!stillValid) {
      setSelectedQueue('')
      setSelectedUser('')
    }
  }, [selectedConnection, connections, queues])

  // useEffect(() => {
  //   let isMounted = true
  //   if (selectedQueue) {
  //     const fetchUsers = async () => {
  //       try {
  //         const { data } = await api.get('/users/list', {
  //           params: { queueIds: [selectedQueue] }
  //         })
  //         console.log('dados dos usuarios referente ao departamento', data)
  //         if (isMounted) setUsers(data)
  //       } catch (err) {
  //         if (isMounted) toastError(err)
  //       }
  //     }
  //     fetchUsers()
  //   } else {
  //     setUsers([])
  //   }
  //   return () => {
  //     isMounted = false
  //   }
  // }, [selectedQueue])

  const fetchUsers = useCallback(async () => {
    if (!selectedQueue) {
      setUsers([])
      setSelectedUser('')
      return
    }

    try {
      const { data } = await api.get('/users/list', {
        params: { queueId: selectedQueue }
      })

      const usersList = Array.isArray(data) ? data : data.users || []

      const filteredUsers = usersList.filter(user => {
        if (Array.isArray(user.queues)) {
          return user.queues.some(
            q => q.id === selectedQueue || q === selectedQueue
          )
        }
        if (Array.isArray(user.queueIds)) {
          return user.queueIds.includes(selectedQueue)
        }
        return true
      })

      setUsers(filteredUsers)

      if (
        selectedUser &&
        !filteredUsers.some(user => user.id === selectedUser)
      ) {
        setSelectedUser('')
      }
    } catch (err) {
      toastError(err)
    }
  }, [selectedQueue, selectedUser])

  useEffect(() => {
    let isMounted = true

    if (selectedQueue) {
      fetchUsers()
    } else {
      setUsers([])
      setSelectedUser('')
    }

    return () => {
      isMounted = false
    }
  }, [selectedQueue, fetchUsers])

  const departmentOptions = useMemo(() => {
    if (!selectedConnection || !queues.length) return []

    const connection = connections.find(c => c.id === selectedConnection)
    if (!connection) return []

    const connectionQueueIds = new Set(
      (connection.queues || []).map(q => q.id)
    )

    // Exibe departamentos reais vinculados diretamente à conexão ou encadeados
    // via botQueueId. Filas bot selecionadas apenas em "Escolher Bots" não devem
    // expor a conexão, mas se a conexão permitida as contém, seus departamentos
    // de destino podem ser usados.
    const botQueueIds = queues
      .filter(q => q.isBot && connectionQueueIds.has(q.id))
      .map(q => q.id)

    return queues.filter(
      q =>
        !q.isBot &&
        !q.notificationact &&
        (connectionQueueIds.has(q.id) || botQueueIds.includes(q.botQueueId))
    )
  }, [selectedConnection, connections, queues])

  const fetchContactDetails = async contactId => {
    try {
      const { data } = await api.get(`/contacts/${contactId}`)

      return data
    } catch (err) {
      console.error('Erro ao buscar detalhes do contato:', err)
      toastError(err)
      return null
    }
  }

  const checkExistingTicket = async (contactId, whatsappId = null) => {
    try {
      const params = whatsappId ? { whatsappId } : {}
      const { data } = await api.get(`/tickets/check-existing/${contactId}`, {
        params
      })
      return data.ticket
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Erro ao verificar ticket existente:', err)
      }
      return null
    }
  }

  const handleRequestTransfer = async (
    ticketId,
    preferredTargetQueueId = selectedQueue
  ) => {
    try {
      await api.post(`/tickets/${ticketId}/request-transfer`, {
        preferredTargetQueueId:
          preferredTargetQueueId !== '' ? preferredTargetQueueId : undefined
      })
      toast.success('Solicitação de transferência enviada com sucesso!')
      setExistingTicketDialogOpen(false)
      handleClose()
    } catch (err) {
      console.error('Erro ao solicitar transferência:', err)
      toastError(err)
    }
  }

  const handleOpenExistingTicket = ticket => {
    setExistingTicketDialogOpen(false)
    handleClose()
    history.push(`/tickets/${ticket.uuid || ticket.id}`)
  }

  const handleAcceptUnassignedTicket = async ticketId => {
    try {
      await api.put(`/tickets/${ticketId}`, {
        status: 'open',
        userId: user?.id
      })
      toast.success('Atendimento aceito com sucesso!')
      setExistingTicketDialogOpen(false)
      handleClose()
      history.push(`/tickets/${ticketId}`)
    } catch (err) {
      console.error('Erro ao aceitar atendimento:', err)
      toastError(err)
    }
  }

  // const createNewContact = async (contactName, contactNumber) => {
  //   try {
  //     // Remove todos os caracteres não numéricos do número
  //     const cleanNumber = contactNumber.replace(/\D/g, '')

  //     const contactData = {
  //       name: contactName,
  //       number: cleanNumber,
  //       email: ''
  //     }

  //     const { data } = await api.post('/contacts', contactData)
  //     return data
  //   } catch (err) {
  //     console.error('Erro ao criar novo contato:', err)
  //     toastError(err)
  //     return null
  //   }
  // }

  const createNewContact = async (contactName, contactNumber) => {
    try {
      const cleanNumber = String(contactNumber || '').replace(/\D/g, '')

      console.log(
        '%c[CONTACT-CREATE]%c Enviando requisição para criar contato:',
        'color: #0ea5e9; font-weight: bold;',
        {
          name: contactName,
          number: cleanNumber,
          whatsappId: selectedConnection
        }
      )

      const contactData = {
        name: contactName,
        number: cleanNumber,
        email: '',
        whatsappId: selectedConnection || null
      }

      const { data } = await api.post('/contacts', contactData)
      return data
    } catch (err) {
      console.error(
        'Falha no servidor ao criar contato:',
        err.response?.data || err.message
      )

      const statusCode = err.response?.status
      const serverError =
        err.response?.data?.error || err.response?.data?.message || ''
      const lowerError = serverError.toLowerCase()

      if (
        statusCode === 500 ||
        lowerError.includes('jid') ||
        lowerError.includes('number') ||
        lowerError.includes('whatsapp')
      ) {
        toast.error(
          'Não foi possível validar o número no WhatsApp. Por favor, verifique o número e tente novamente.'
        )
      } else {
        toast.error(
          serverError ||
          'Ocorreu um erro inesperado ao cadastrar o contato. Tente novamente mais tarde.'
        )
      }

      return null
    }
  }

  const checkExistingContact = async phoneNumber => {
    try {
      const cleanNumber = phoneNumber.replace(/\D/g, '')

      // Buscar contato por número
      const { data } = await api.get('contacts', {
        params: { searchParam: cleanNumber }
      })

      // Procurar contato com número exato ou variações comuns
      const exactMatch = data.contacts.find(contact => {
        const contactNumber = contact.number.replace(/\D/g, '')

        // Comparar números exatos
        if (contactNumber === cleanNumber) return true

        // Comparar sem código do país (55 para Brasil)
        if (
          contactNumber.startsWith('55') &&
          contactNumber.substring(2) === cleanNumber
        )
          return true
        if (
          cleanNumber.startsWith('55') &&
          cleanNumber.substring(2) === contactNumber
        )
          return true

        return false
      })

      if (exactMatch) {
        // Buscar detalhes completos do contato
        const contactData = await fetchContactDetails(exactMatch.id)
        return contactData || exactMatch
      }

      return null
    } catch (err) {
      console.error('Erro ao verificar contato existente:', err)
      return null
    }
  }

  const handleTransferTicket = async () => {
    setLoading(true)
    try {
      if (!ticketId) {
        toastError('ID do ticket não encontrado para transferência.')
        setLoading(false)
        return
      }
      if (!selectedQueue) {
        toast.error('Selecione um departamento.')
        setLoading(false)
        return
      }

      const updateData = {
        status: 'pending',
        queueId: selectedQueue,
        userId: selectedUser || null
      }

      await api.put(`/tickets/${ticketId}`, updateData)

      // toast.success('Atendimento transferido com sucesso!')
      setLoading(false)
      onClose()
      history.push(`/tickets`)
    } catch (err) {
      setLoading(false)
      toastError(err)
    }
  }

  const handleSaveTicket = async contactId => {
    if (isEditing) {
      setLoading(true)
      try {
        await handleTransferTicket()
      } finally {
        setLoading(false)
      }
      return
    }

    if (!contactId && !newContact && (!name || !number)) return
    // Verificar se deve criar um novo contato
    if (
      !contactId &&
      name &&
      number &&
      name.trim() !== '' &&
      number.trim() !== ''
    ) {
      if (!validatePhone(number)) {
        setModalOpenWarning(true)
        return
      }

      setLoading(true)
      try {
        // Verificar se já existe um contato com este número
        const fullNumber = countryCode + number
        const cleanNumber = fullNumber.replace(/\D/g, '')

        const existingContact = await checkExistingContact(cleanNumber)

        if (existingContact) {
          // Se o contato já existe, verificar se há tickets abertos na mesma conexão
          const existingTicketData = await checkExistingTicket(
            existingContact.id,
            selectedConnection
          )

          if (existingTicketData) {
            // Se há um ticket aberto na mesma conexão, mostrar o diálogo
            setExistingTicket(existingTicketData)
            setExistingTicketDialogOpen(true)
            setLoading(false)
            return
          }

          // Se não há ticket aberto, usar o contato existente
          toast.info(
            `Contato "${existingContact.name}" já existe. Abrindo atendimento para o contato existente.`
          )
          setSelectedContact(existingContact)
          setName(existingContact.name)
          setNewContact(false)

          if (existingContact.tags) {
            setContactTags(existingContact.tags)
          }

          // Continuar com a criação do ticket usando o contato existente
          await createTicketForContact(existingContact.id, existingContact)
        } else {
          // Se não existe, criar novo contato
          const newContactData = await createNewContact(name, fullNumber)

          if (newContactData) {
            // Atualizar estados com o novo contato
            setSelectedContact(newContactData)
            setNewContact(false)

            // Continuar com a criação do ticket usando o ID do novo contato
            await createTicketForContact(newContactData.id, newContactData)
          }
        }
      } catch (err) {
        console.error('Erro ao criar contato e ticket:', err)
        toastError(err)
      } finally {
        setLoading(false)
      }
      return
    }

    // Lógica para contatos existentes selecionados
    if (contactId) {
      // Verificar se há tickets abertos para este contato na mesma conexão
      const existingTicketData = await checkExistingTicket(
        contactId,
        selectedConnection
      )

      if (existingTicketData) {
        // Se há um ticket aberto na mesma conexão, mostrar o diálogo
        setExistingTicket(existingTicketData)
        setExistingTicketDialogOpen(true)
        return
      }
    }

    // Lógica original para contatos existentes ou quando não há dados suficientes para criar novo contato
    if (!contactId && !newContact) return
    if (selectedConnection === '') {
      toast.error('Selecione um canal de conexão')
      return
    }
    if (selectedQueue === '') {
      toast.error('Selecione um departamento')
      return
    }

    let phoneToValidate = newContact ? number : selectedContact?.number || ''

    if (newContact) {
      if (!validatePhone(phoneToValidate)) {
        setModalOpenWarning(true)
        return
      }
    }

    setLoading(true)
    await createTicketForContact(contactId, selectedContact)
  }

  const createTicketForContact = async (contactId, contactData) => {
    try {
      if (selectedQueue === '') {
        toast.error('Selecione um departamento')
        setLoading(false)
        return
      }

      const queueId = selectedQueue !== '' ? selectedQueue : null

      let updatedContactTags = contactTags
      if (
        contactId &&
        (!contactTags || contactTags.length === 0) &&
        !newContact
      ) {
        const fetchedContactData = await fetchContactDetails(contactId)
        if (fetchedContactData && fetchedContactData.tags) {
          updatedContactTags = fetchedContactData.tags
          setContactTags(fetchedContactData.tags)
        }
      }

      const newTicketData = {
        contactId: contactId,
        contactName: contactData?.name || name,
        whatsappId: selectedConnection,
        queueId,
        userId: selectedUser || null,
        status: selectedUser && selectedUser !== '' ? 'open' : 'pending',
        contactTags: updatedContactTags
      }

      const { data: ticket } = await api.post('/tickets', newTicketData)

      // Informar o usuário sobre o status do ticket
      if (!selectedUser || selectedUser === '') {
        toast.info(
          'Atendimento criado com sucesso! Status: PENDENTE no departamento até ser atribuído a um atendente.'
        )
      } else {
        toast.success(
          'Atendimento criado com sucesso! Status: ABERTO e atribuído ao atendente.'
        )
      }

      if (updatedContactTags && updatedContactTags.length > 0) {
        const contactOnlyTags = updatedContactTags.filter(
          tag => tag && (tag.tipo === 'contato' || !tag.tipo)
        )

        const contactEvent = new CustomEvent('contactTagsChanged', {
          detail: {
            contactId: contactId,
            tags: contactOnlyTags
          }
        })
        window.dispatchEvent(contactEvent)

        const combinedEvent = new CustomEvent('combinedTagsChanged', {
          detail: {
            ticketId: ticket.id,
            contactId: contactId,
            contactTags: contactOnlyTags,
            ticketTags: [],
            allTags: contactOnlyTags
          }
        })
        window.dispatchEvent(combinedEvent)

        const ticketEvent = new CustomEvent('ticketTagsChanged', {
          detail: {
            ticketId: ticket.id,
            tags: []
          }
        })
        window.dispatchEvent(ticketEvent)

        // Evento forçando atualização de UI
        setTimeout(() => {
          const refreshEvent = new CustomEvent('forceTicketListRefresh', {
            detail: {
              ticketId: ticket.id,
              contactId: contactId
            }
          })
          window.dispatchEvent(refreshEvent)
        }, 100)
      }

      onClose(ticket)
    } catch (err) {
      console.error('Erro ao salvar ticket:', err)
      toastError(err)
    }
    setLoading(false)
  }

  const handleSelectOption = async contact => {
    // Remova o 'e' se estiver vindo direto do onSelected
    if (!contact) {
      setIsNewContactFlow(true)
      setSelectedContact(null)
      setName('')
      setNumber('')
      return
    }

    // Se for um contato existente (tem número)
    if (contact.number) {
      setIsNewContactFlow(false)

      // Atualiza imediatamente os campos de texto para refletir a escolha
      setName(contact.name || '')
      setNumber(contact.number || '')
      setSelectedContact(contact)

      if (contact.id) {
        const contactData = await fetchContactDetails(contact.id)
        if (contactData) {
          setSelectedContact(contactData)
          setName(contactData.name || '')
          // Garanta que o número seja atualizado aqui também
          setNumber(contactData.number || '')
          if (contactData.tags) setContactTags(contactData.tags)
        }
      }
      setNewContact(false)
    }
    // Se for a opção de "Criar Novo" vinda do autocomplete
    else if (contact.name) {
      setIsNewContactFlow(true)
      setName(contact.name)
      setNumber('')
      setNewContact(true)
      setContactTags([])
    }
  }

  const searchNumber = async number => {
    if (!number.startsWith('+')) {
      number = `${countryCode}${number}`
    }
    const parsedPhone = parsePhoneNumberFromString(number)
    number = number.replace('+', '')

    if (parsedPhone) {
      setCountryCode(`+${parsedPhone.countryCallingCode}`)
    }

    if (number.length < 3) return

    try {
      const { data } = await api.get('contacts', {
        params: { searchParam: number }
      })
      const contact = data.contacts.find(c => c.number === number)
      if (contact) {
        const contactData = await fetchContactDetails(contact.id)
        if (contactData) {
          setSelectedContact(contactData)
          setName(contactData.name)

          if (contactData.tags) {
            setContactTags(contactData.tags)
          }
        } else {
          setSelectedContact(contact)
        }

        setNewContact(false)
      } else {
        setNewContact(true)
        setContactTags([])
      }
    } catch (err) {
      console.error('LOG: Erro na busca por número:', err)
      toastError(err)
    }
  }

  const renderContactAutocomplete = () => {
    if (isEditing) {
      return null
    }
    if (
      !initialContact ||
      initialContact.id === undefined ||
      initialContact.id === null
    ) {
      return (
        <Grid container spacing={1}>
          <Grid xs={12} item>
            <ContactSelect
              label={i18n.t('newTicketModal.fieldLabel')}
              allowCreate={true}
              placeholder="Pesquise um contato"
              onSelected={contact => handleSelectOption(contact)}
              onCreateContact={newName => {
                setIsNewContactFlow(true)
                setNewContact(true)
                setName(newName)
                setNumber('')
                setSelectedContact(null)

                setTimeout(() => {
                  if (phoneInputWrapperRef.current) {
                    // Busca o campo de texto nativo dentro do wrapper
                    const phoneInput =
                      phoneInputWrapperRef.current.querySelector(
                        'input[type="text"], input[type="tel"]'
                      )
                    if (phoneInput) {
                      phoneInput.focus()
                    }
                  }
                }, 150)
              }}
            />
          </Grid>
        </Grid>
      )
    }

    return null
  }

  const validatePhone = phone => {
    // Remove todos os caracteres não numéricos e valida o comprimento
    const phoneNumber = String(phone || '').replace(/\D/g, '')

    console.log('[PHONE-VALIDATION] Validando telefone:', {
      inputOriginal: phone,
      telefoneLimpo: phoneNumber,
      tamanho: phoneNumber.length
    })

    const isValid = phoneNumber.length >= 10 && phoneNumber.length <= 11

    if (!isValid) {
      console.log(
        '[PHONE-VALIDATION] Falha na validação - Fora do intervalo permitido (10 a 13 dígitos)'
      )
    }

    return isValid
  }

  const warningModal = () => {
    return (
      <Dialog
        open={modalOpenWarning}
        onClose={() => setModalOpenWarning(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialogWarning}
      >
        <DialogTitle
          id="alert-dialog-title"
          className={classes.dialogWarningTitle}
        >
          <div className={classes.warningContainer}>
            <TriangleAlert size={34} />
            <span className={classes.warningTitle}>Atenção!</span>
          </div>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <span className={classes.warningDescription}>
            O número de telefone deve conter entre 10 e 11 dígitos, incluindo o
            código de área (DDD), e deve ser informado sem espaços, traços ou
            parênteses, seguindo o formato: 67912345678.
          </span>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <ButtonPrimary
            onClick={() => setModalOpenWarning(false)}
            color="primary"
            label="Entendi"
          />
        </DialogActions>
      </Dialog>
    )
  }

  if (!modalOpen) {
    return null
  }

  const getFormattedPhoneValue = () => {
    if (selectedContact) {
      return `+${String(selectedContact.number || '').replace(/\D/g, '')}`
    }
    return countryCode && number ? `${countryCode}${number}` : ''
  }

  const handlePhoneChange = data => {
    if (data && typeof data === 'object') {
      const cleanPhone = data.telefone
        ? String(data.telefone).replace(/\D/g, '')
        : ''
      setNumber(cleanPhone)

      if (data.countryCode) {
        const formatCountryCode = String(data.countryCode).startsWith('+')
          ? data.countryCode
          : `+${data.countryCode}`
        setCountryCode(formatCountryCode)
      }
    }
  }

  const handlePhoneBlur = data => {
    if (data && typeof data === 'object' && data.telefone) {
      const cleanPhone = String(data.telefone).replace(/\D/g, '')
      if (cleanPhone) {
        searchNumber(cleanPhone)
      }
    }
  }

  return (
    <div className={classes.root}>
      <Dialog open={modalOpen} onClose={handleClose} scroll="paper">
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          {isEditing ? 'Aceitar Atendimento' : 'Novo Atendimento'}
          {selectedConnection && connections.length > 0 && (
            <div
              style={{
                fontSize: '12px',
                fontWeight: 'normal',
                color: '#666',
                marginTop: '4px'
              }}
            >
              Verificando atendimentos existentes apenas na conexão:{' '}
              {connections.find(c => c.id === selectedConnection)?.name}
            </div>
          )}
        </DialogTitle>

        <Formik initialValues={newTicket} enableReinitialize={true}>
          <Form>
            <DialogContent dividers className={classes.dialogContent}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={12}>
                  <SelectInput
                    label="Canal"
                    value={selectedConnection}
                    onChange={e => setSelectedConnection(e.target.value)}
                  >
                    <MenuItem value="">Selecione</MenuItem>
                    {connections.map((connection, key) => (
                      <MenuItem key={key} value={connection.id}>
                        {connection.name}
                      </MenuItem>
                    ))}
                  </SelectInput>
                </Grid>
                <Grid item xs={12} md={12} sm={12}>
                  {renderContactAutocomplete()}
                </Grid>

                <Grid item xs={12} className={classes.inputWrapperPhone}>
                  <Grid item xs={5} ref={phoneInputWrapperRef}>
                    <UserPhoneCountry
                      label="Telefone"
                      value={getFormattedPhoneValue()}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneBlur}
                      disabled={
                        isEditing || !isNewContactFlow || !!selectedContact
                      }
                    />
                  </Grid>

                  <Grid item xs={7}>
                    <InputField
                      name="name"
                      label={i18n.t('contactModal.form.name')}
                      value={name}
                      onChange={e => setName(e.target.value)}
                      disabled={isEditing || !isNewContactFlow}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <SelectInput
                    label="Departamento"
                    value={selectedQueue || ''}
                    onChange={e => setSelectedQueue(e.target.value)}
                  >
                    <MenuItem value="" className={classes.menuItem}>
                      Selecione
                    </MenuItem>
                    {departmentOptions?.length > 0 &&
                      departmentOptions.map((queue, key) => (
                        <MenuItem
                          dense
                          key={key}
                          value={queue.id}
                          className={classes.menuItem}
                        >
                          <Radio
                            checked={queue.id === selectedQueue}
                            className={classes.radio}
                          />
                          {queue.name}
                        </MenuItem>
                      ))}
                  </SelectInput>
                </Grid>

                {selectedQueue && (
                  <Grid item xs={12} sm={12} md={12}>
                    <SelectInput
                      label="Atendente (Opcional)"
                      value={selectedUser}
                      onChange={e => setSelectedUser(e.target.value)}
                      multiple={false}
                    >
                      <MenuItem value="" className={classes.menuItem}>
                        <Radio
                          checked={!selectedUser}
                          className={classes.radio}
                        />
                        Disponível
                      </MenuItem>
                      {users.map((user, key) => (
                        <MenuItem
                          key={key}
                          value={user.id}
                          className={classes.menuItem}
                        >
                          <Radio
                            checked={user.id === selectedUser}
                            className={classes.radio}
                          />
                          <span>{user.name}</span>
                          <div className={classes.statusContainer}>
                            <span
                              className={
                                user.online
                                  ? classes.statusDotOnline
                                  : classes.statusDotOffline
                              }
                            />
                            <span className={classes.userStatus}>
                              {user.online ? '(On)' : '(Off)'}
                            </span>
                          </div>
                        </MenuItem>
                      ))}
                    </SelectInput>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
          </Form>
        </Formik>
        <DialogActions className={classes.dialogActions}>
          <ButtonSecondary
            onClick={handleClose}
            color="secondary"
            disabled={loading}
            label={i18n.t('newTicketModal.buttons.cancel')}
          />
          <ButtonPrimary
            label={isEditing ? 'Aceitar' : 'Iniciar'}
            disabled={
              loading ||
              (isEditing
                ? !selectedQueue
                : (!selectedContact && (!name || !number)) ||
                !selectedConnection ||
                !selectedQueue)
            }
            onClick={() => handleSaveTicket(selectedContact?.id)}
            endIcon={<SendHorizontal />}
          />
        </DialogActions>
      </Dialog>
      {warningModal()}

      <ExistingTicketDialog
        open={existingTicketDialogOpen}
        onClose={() => setExistingTicketDialogOpen(false)}
        existingTicket={existingTicket}
        onRequestTransfer={ticketId =>
          handleRequestTransfer(ticketId, selectedQueue)
        }
        onAcceptTicket={handleAcceptUnassignedTicket}
        onOpenTicket={handleOpenExistingTicket}
      />
    </div>
  )
}

export default NewTicketModal
